import { useEffect, useRef, useState } from 'react';
import { Maximize2, Play, Video } from 'lucide-react';
import type { MessageSource } from '../api/conversation';
import { useT } from '../I18nContext';

/**
 * One video citation tile beneath an assistant message.
 *
 * Two states:
 *   - **Collapsed (default).** Thumbnail (or generic icon if no
 *     thumb) + title + course attribution + timestamp badge + play
 *     button. Click anywhere on the tile to expand.
 *   - **Expanded.** Inline `<video>` element with native controls,
 *     pre-seeked to `start_time` via the `#t=` URL fragment. A
 *     small fullscreen button overlays the player (the native
 *     fullscreen button on Safari's mini-player is finicky to
 *     trigger from inside a card, so we expose our own).
 *
 * Why inline rather than modal: the message-thread context is
 * useful - users often want to re-read the question + the
 * snippet under the player. A modal would obscure that.
 *
 * Signed-URL expiry: PR #127 ships 1h signed `play_url` /
 * `thumb_url` URLs on every chat response. If a user opens a stale
 * conversation past the expiry, the inline player will fail to
 * load - we silently catch that on the `<video>` `onError` and
 * surface a friendly retry-with-refresh message rather than the
 * native broken-video icon. (Future PR can re-fetch via
 * /api/platform/videos/<id>/play-url; for v1 we point them at the
 * Plan modal's video page instead.)
 */
interface Props {
  source: MessageSource;
  /** Optional controlled-mode expanded state. When provided, the
   *  tile is fully controlled by the parent (used by MessageSources
   *  in pill-mode so the global SourcePanelContext can enforce
   *  one-source-open-at-a-time). When omitted, the tile falls back
   *  to its own internal state - preserves the legacy
   *  display_video_inline=true behaviour where the tile is always
   *  rendered and self-manages its expand toggle. */
  expanded?: boolean;
  onCollapse?: () => void;
}

export function VideoCitationTile({ source, expanded: controlledExpanded, onCollapse }: Props) {
  const t = useT();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;
  const [playerError, setPlayerError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // When the user collapses, pause playback so audio doesn't bleed
  // through. The browser stops the network fetch automatically on
  // unmount; this is the explicit-pause-on-collapse path.
  useEffect(() => {
    if (!expanded && videoRef.current) {
      try { videoRef.current.pause(); } catch { /* never throws meaningfully */ }
    }
  }, [expanded]);

  const startTime = Math.max(0, Math.floor(source.start_time || 0));
  const startDisplay = source.start_time_display || formatSeconds(startTime);
  const title = source.title || t('message.source_video_default');
  const course = source.course_title || null;
  const playUrl = source.play_url || '';
  const thumbUrl = source.thumb_url || '';
  const snippet = source.snippet || '';

  // Append the `#t=N` fragment so the browser seeks before playing.
  // `Element.requestFullscreen()` is invoked on the <video> tag for
  // the fullscreen toggle - same DOM element, just a JS call.
  const seekUrl = playUrl ? `${playUrl}#t=${startTime}` : '';

  function expand() {
    if (!playUrl) {
      setPlayerError('This video is unavailable right now. Reload the conversation to refresh the link.');
      return;
    }
    setPlayerError(null);
    if (!isControlled) {
      setUncontrolledExpanded(true);
    }
    // In controlled mode the parent decides when to expand; the
    // pill click in MessageSources calls openVideo() directly, not
    // this function. So nothing to do here other than clear errors.
  }

  function collapse() {
    if (isControlled) {
      onCollapse?.();
    } else {
      setUncontrolledExpanded(false);
    }
  }

  async function goFullscreen() {
    if (!videoRef.current) return;
    try {
      await videoRef.current.requestFullscreen();
    } catch {
      // Some browsers / iframes block it; fall back to expanding
      // the player and letting the user use the native control.
    }
  }

  // Controlled mode: the parent renders the pill button, so we only
  // show the tile when expanded == true. Returning null lets the
  // wrapper element stay out of the DOM entirely until needed.
  if (isControlled && !expanded) {
    return null;
  }

  return (
    <div className={'video-citation-tile' + (expanded ? ' is-expanded' : '')}>
      {!expanded && (
        <button
          type="button"
          className="video-citation-tile-cover"
          onClick={expand}
          aria-label={`Play ${title} at ${startDisplay}`}
        >
          {thumbUrl ? (
            <img
              className="video-citation-tile-thumb"
              src={thumbUrl}
              alt=""
              loading="lazy"
              onError={(e) => {
                // Thumbnail URL expired or 404. Hide the broken
                // image element so the play-icon fallback shows.
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="video-citation-tile-thumb video-citation-tile-thumb-empty">
              <Video size={24} aria-hidden />
            </div>
          )}

          <div className="video-citation-tile-play-overlay" aria-hidden>
            <Play size={20} />
          </div>

          <span className="video-citation-tile-badge">{startDisplay}</span>
        </button>
      )}

      <div className="video-citation-tile-meta">
        <div className="video-citation-tile-title">{title}</div>
        {course && (
          <div className="video-citation-tile-course">from {course}</div>
        )}
        {snippet && !expanded && (
          <div className="video-citation-tile-snippet">{snippet}</div>
        )}
      </div>

      {expanded && (
        <div className="video-citation-tile-player">
          {seekUrl && (
            <video
              ref={videoRef}
              src={seekUrl}
              controls
              autoPlay
              preload="metadata"
              playsInline
              onError={() => setPlayerError(
                'Could not load the video. The playback link may have expired.'
              )}
            />
          )}
          {playerError && (
            <div className="video-citation-tile-error" role="alert">
              {playerError}
            </div>
          )}
          <div className="video-citation-tile-player-actions">
            <button
              type="button"
              className="video-citation-tile-action"
              onClick={goFullscreen}
              aria-label={t('video.open_fullscreen')}
            >
              <Maximize2 size={14} /> Fullscreen
            </button>
            <button
              type="button"
              className="video-citation-tile-action video-citation-tile-action-secondary"
              onClick={collapse}
              aria-label={t('video.collapse')}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function formatSeconds(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}
